import {
  DeliveryPickerWrapper,
  StyledBackButton,
  StyledDeliveryBoxWrapper,
  StyledDeliveryPickerHeading,
  StyledErrorText,
} from './DeliverySelector.styles';
import DeliveryBox from '@molecules/DeliveryBox/DeliveryBox';
import Icon from '@atoms/Icon/Icon';
import Store from '@icons/store.svg';
import Text from '@atoms/Text/Text';
import HomeDelivery from '@icons/home-delivery.svg';
import React, { Dispatch, Key, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Config from '@config';
import { useTranslations } from 'next-intl';
import SlotSelector from '@organisms/SlotSelector/SlotSelector';
import ArrowLeft from '@icons/arrow-left-small.svg';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import {
  selectCheckout,
  selectPossiblyWrongStoreConfirmed,
  setErrorMessage,
  setPossiblyWrongStoreConfirmed,
} from '@slices/checkoutSlice';
import { PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useCustomer from '@hooks/useCustomer';
import PickupInStoreSearch from '@components/molecules/PickupInStoreSearch/PickupInStoreSearch';
import { trackDeliveryPickerOpened } from '@helpers/analyticsHelpers/trackDeliveryPicker';
import { selectDeliveryPicker } from '@slices/deliveryPickerSlice';
import FullAddressSearch from '@components/molecules/FullAddressSearch/FullAddressSearch';
import {
  getDeliveryPickerBoxHomeDeliveryKey,
  getDeliveryPickerErrorKey,
  getDeliveryPickerTitleKey,
} from '@helpers/deliverypickerTranslation';

export type DeliveryPickerNavigationStateType = 'chooseDeliveryMode' | 'pickUpInStore' | 'homeDelivery' | 'chooseSlot';

interface Props {
  setShowPickedSlotNotification?: Dispatch<SetStateAction<boolean>>;
}

const DeliverySelector = ({ setShowPickedSlotNotification }: Props) => {
  const t = useTranslations('deliverypicker');
  const { store } = useStore();
  const { cart } = useCart();
  const autoFocusRef = useRef<HTMLDivElement>(null);
  const deliveryPicker = useAppSelector(selectDeliveryPicker);
  const [selectedDeliveryMode, setSelectedDeliveryMode] = useState(
    cart?.deliveryModeCode ? cart.deliveryModeCode : 'chooseDeliveryMode'
  );
  const dispatch = useAppDispatch();
  const possiblyWrongStoreConfirmed = useAppSelector(selectPossiblyWrongStoreConfirmed);
  const checkout = useAppSelector(selectCheckout);
  const { customer } = useCustomer();
  const [navigationState, setNavigationState] = useState<DeliveryPickerNavigationStateType>(
    selectedDeliveryMode as DeliveryPickerNavigationStateType
  );

  useEffect(() => {
    if (deliveryPicker.isOpen) {
      trackDeliveryPickerOpened(deliveryPicker.trackingSource, Boolean(cart?.deliveryModeCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryPicker.isOpen]);

  const isOpenOrderAndFranchise = useMemo(
    () => Boolean(cart?.orderReference) && cart?.franchiseStore,
    [cart?.franchiseStore, cart?.orderReference]
  );

  const initShowSlotSelector = () => {
    const showSlots = store?.storeId && store?.activelySelected && !!cart?.deliveryModeCode;
    const isKPLStore = store?.external;

    switch (selectedDeliveryMode) {
      case Config.HOME_DELIVERY:
        return showSlots && cart?.deliveryAddress?.postalCode;
      case Config.PICKUP_IN_STORE:
        return showSlots && !isKPLStore;
      default:
        return false;
    }
  };
  const [showSlotSelector] = useState(initShowSlotSelector());

  const onTabChange = (tab: Key) => {
    const modeCode = tab === Config.HOME_DELIVERY ? Config.HOME_DELIVERY : Config.PICKUP_IN_STORE;
    setNavigationState(modeCode as DeliveryPickerNavigationStateType); // Safe type assertion
    setSelectedDeliveryMode(modeCode);
  };

  useEffect(() => {
    if (navigationState === 'chooseDeliveryMode' && autoFocusRef.current) {
      setTimeout(() => autoFocusRef.current?.focus(), 0);
    }
  }, [navigationState]);

  useEffect(() => {
    if (showSlotSelector) {
      setNavigationState('chooseSlot');
    }
  }, [showSlotSelector]);

  useEffect(() => {
    if (isOpenOrderAndFranchise) {
      setNavigationState('chooseSlot');
    }
  }, [isOpenOrderAndFranchise]);

  const BackButton = (state: DeliveryPickerNavigationStateType) =>
    !isOpenOrderAndFranchise && (
      <StyledBackButton
        variant="transparent"
        onClick={() => setNavigationState(state)}
        data-testid="navigate-back-button"
      >
        <Icon svg={ArrowLeft} size={16} />
      </StyledBackButton>
    );

  return (
    <DeliveryPickerWrapper data-testid="delivery-picker-widget">
      <StyledDeliveryPickerHeading variant="h3">
        {t(getDeliveryPickerTitleKey(navigationState, Boolean(customer?.isB2BCustomer)))}
      </StyledDeliveryPickerHeading>

      {navigationState === 'chooseDeliveryMode' && (
        <StyledDeliveryBoxWrapper>
          <DeliveryBox
            onClick={() => onTabChange(Config.PICKUP_IN_STORE)}
            icon={<Icon size={24} svg={Store} />}
            data-testid="delivery-method-pickUpInStore"
            ref={autoFocusRef}
          >
            <Text type="subtitle" size="medium">
              {t('box.pickUpInStore')}
            </Text>
            <Text type="body" size="medium">
              {t('price.pickUpInStore')}
            </Text>
          </DeliveryBox>

          <DeliveryBox
            onClick={() => onTabChange(Config.HOME_DELIVERY)}
            icon={<Icon size={24} svg={HomeDelivery} />}
            data-testid="delivery-method-homeDelivery"
          >
            <Text type="subtitle" size="medium">
              {t(getDeliveryPickerBoxHomeDeliveryKey(Boolean(customer?.isB2BCustomer)))}
            </Text>
            <Text type="body" size="medium">
              {t('price.homeDelivery')}
            </Text>
          </DeliveryBox>
        </StyledDeliveryBoxWrapper>
      )}

      {navigationState === Config.PICKUP_IN_STORE && (
        <>
          {BackButton('chooseDeliveryMode')}
          <PickupInStoreSearch
            afterSubmit={() => {
              setNavigationState('chooseSlot');
              if (possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.UNCONFIRMED) {
                dispatch(setPossiblyWrongStoreConfirmed(PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY));
              }
            }}
          />
        </>
      )}

      {navigationState === Config.HOME_DELIVERY && (
        <>
          {BackButton('chooseDeliveryMode')}
          <FullAddressSearch afterSubmit={() => setNavigationState('chooseSlot')} />
        </>
      )}

      {navigationState === 'chooseSlot' && (
        <>
          {BackButton(selectedDeliveryMode as DeliveryPickerNavigationStateType)}
          {checkout.delivery.errorMessage &&
            (() => {
              const errorKey = getDeliveryPickerErrorKey(checkout.delivery.errorMessage);
              return errorKey ? (
                <StyledErrorText type="body" size="medium" color="error">
                  {t(errorKey)}
                </StyledErrorText>
              ) : null;
            })()}
          <SlotSelector
            deliveryMode={selectedDeliveryMode as 'homeDelivery' | 'pickUpInStore'}
            changeDeliveryHandler={() => setNavigationState('chooseDeliveryMode')}
            onSubmitted={() => {
              if (
                cart?.deliveryModeCode === Config.PICKUP_IN_STORE &&
                possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.UNCONFIRMED
              ) {
                dispatch(setPossiblyWrongStoreConfirmed(PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY));
              }
              dispatch(setErrorMessage(''));

              if (setShowPickedSlotNotification) setShowPickedSlotNotification(true);
            }}
          />
        </>
      )}
    </DeliveryPickerWrapper>
  );
};
export default DeliverySelector;
